import { Core } from "@faire/design-tokens";
import careersPath from "@faire/web-api/routes/careers";
import belongingPath from "@faire/web-api/routes/careers/belonging-at-faire";
import jobOpeningsPath from "@faire/web-api/routes/careers/openings";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { ArrowRight } from "@faire/web/slate/icons/Arrow";
import { Row } from "@faire/web/slate/Layout";
import { FaireLogo } from "@faire/web/slate/logos/FaireLogo";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { removeLocaleFromUrl } from "@faire/web/ui/routing/util";
import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

import { HeaderContainer } from "@faire/retailer/components/Header/styled";
import { home } from "@faire/retailer/paths";

interface ILink {
  path: string;
  label: string;
}

export const HeaderCareersDesktop: React.FC = () => {
  const { pathname: pathnameWithMaybeLocale } = useLocation();
  const pathname = useMemo(
    () => removeLocaleFromUrl(pathnameWithMaybeLocale),
    [pathnameWithMaybeLocale]
  );
  const isOnOpeningsPage = pathname === jobOpeningsPath();
  const links = useLinks();

  return (
    <StyledHeaderContainer>
      <FullWidthRow>
        <Row align="center">
          <Spacer width="8x" />
          <Link to={home()}>
            <FaireLogo />
          </Link>
        </Row>
        <Row align="center" justify="flex-end">
          {links.map((link, index) => {
            return (
              <React.Fragment key={link.label}>
                {index ? <Spacer width="4x" /> : null}
                {index < links.length - 1 || isOnOpeningsPage ? (
                  <NoUnderlineButton
                    $active={pathname === link.path}
                    variant="tertiary"
                    linkTo={link.path}
                  >
                    {link.label}
                  </NoUnderlineButton>
                ) : null}
              </React.Fragment>
            );
          })}
          {isOnOpeningsPage ? (
            <Spacer width={118} />
          ) : (
            <>
              <SetWidthCTAButton linkTo={jobOpeningsPath()}>
                <SignUpButtonContent>
                  <StrictLocalMsg
                    defaultMessage="All Job Openings"
                    description={{
                      text: "Button text to navigate to a job openings page",
                    }}
                  />
                  <StyledArrowRight />
                </SignUpButtonContent>
              </SetWidthCTAButton>
              <Spacer width="8x" />
            </>
          )}
        </Row>
      </FullWidthRow>
    </StyledHeaderContainer>
  );
};

const useLinks = (): ILink[] => {
  const { strictLocalize } = useStrictLocalization();
  return [
    {
      path: careersPath(),
      label: strictLocalize({
        defaultMessage: "Life at Faire",
        description: {
          text: "The tab for choosing life at faire",
          img: "https://cdn.faire.com/fastly/55b1c8ed74c991b19c51463c5edd407751a784a4e28aa726b13ddb77b522cdee.png",
        },
      }),
    },
    {
      path: belongingPath(),
      label: strictLocalize({
        defaultMessage: "Belonging at Faire",
        description: {
          text: "The tab for belonging at Faire",
          img: "https://cdn.faire.com/fastly/55b1c8ed74c991b19c51463c5edd407751a784a4e28aa726b13ddb77b522cdee.png",
        },
      }),
    },
    {
      path: jobOpeningsPath(),
      label: strictLocalize({
        defaultMessage: "All Job Openings",
        description: {
          text: "The tab for All Job Openings",
          img: "https://cdn.faire.com/fastly/55b1c8ed74c991b19c51463c5edd407751a784a4e28aa726b13ddb77b522cdee.png",
        },
      }),
    },
  ];
};

const StyledArrowRight = styled(ArrowRight)`
  opacity: 0;
  transition: 0.5s ease;
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto 0;
  right: 2px;
`;

const SignUpButtonContent = styled.span`
  transition: 0.5s ease;
  position: relative;
  padding: 0 10px;
`;

const CTAButton = styled(Button)`
  width: fit-content;
  min-width: 220px;
`;

const StyledCTAButton = styled(CTAButton)<{
  $isSecondary?: boolean;
}>`
  ${(props) =>
    props.$isSecondary
      ? css`
          border: none;
        `
      : ""}
  &:hover {
    ${SignUpButtonContent} {
      padding: 0 20px 0 0;
    }

    ${StyledArrowRight} {
      opacity: 1;
      right: 0;
    }
  }
`;

const NoUnderlineButton = styled(Button)<{ $active: boolean }>`
  border-bottom: ${(props) => (props.$active ? "1px " : "0px")} solid
    ${Core.border.default};
  text-decoration: none;
  &:hover {
    text-decoration: none;
  }
`;

const FullWidthRow = styled(Row).attrs({
  justify: "space-between",
})`
  width: 100%;
`;

const StyledHeaderContainer = styled(HeaderContainer)`
  height: ${getSpacing("8x")};
  flex-direction: row;
`;

export const SetWidthCTAButton = styled(StyledCTAButton)`
  padding: 0px ${getSpacing("2x")};
  min-width: auto;
  height: ${getSpacing("5x")};
`;
